import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { genreNameMap } from '@/mocks/genres';

type HeroCarouselProps = {
  items: Movie[];
};

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = items.length;

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const active = items[index];

  const goPrev = () => setIndex((prev) => (prev - 1 + total) % total);
  const goNext = () => setIndex((prev) => (prev + 1) % total);

  return (
    <section className="relative h-[460px] w-full overflow-hidden bg-primary-900 md:h-[560px] lg:h-[620px]">
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 焦点海报`}
            title={`${movie.title} 焦点推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-end px-4 pb-16 md:px-6 md:pb-20 lg:items-center lg:pb-0">
        <div key={active.id} className="w-full max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-fill" /> 本周焦点 · 评分 {active.rating.toFixed(1)}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-2 font-label text-sm uppercase tracking-widest text-background-300 md:text-base">
            {active.titleEn}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
            <span>{active.year}</span>
            <span>{active.region}</span>
            <span>{active.duration} 分钟</span>
            {active.genres.map((genre) => (
              <span key={genre} className="rounded-full bg-background-50/15 px-2 py-0.5 text-xs">
                {genreNameMap[genre] ?? genre}
              </span>
            ))}
          </div>
          <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {active.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/movie/${active.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line text-lg" /> 查看影片详情
            </Link>
            <Link
              to="/movies/popular"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览热门推荐
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={goPrev}
        className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/40 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={goNext}
        className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/40 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {items.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
}